// i18next-extract-mark-ns-start FAQ

import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Title, mobileThresholdPixels, frames, colors, tabletThresholdPixels, margins,
} from './home/v4/styledComponents';
import withAppContext from '../withAppContext';
import Chevron from '../assets/faq/collapseChevron.svg';
import {
  renderColissimoInsurance, renderMondialRelayInsurance, renderPackagePrice, toFormattedBoldAndItalicText,
} from './home/Question';

const Container = styled.div<{ marginTop?: boolean, noMarginBottom?: boolean }>`
  margin: ${(props) => (props.marginTop ? '120px' : '0')} ${frames.desktop} ${margins.m15};
  max-width: 1180px;
  width: 100%;
  ${(props) => props.noMarginBottom && 'margin-bottom: 0;'}
  @media (max-width: ${tabletThresholdPixels}) {
    margin: ${(props) => (props.marginTop ? '40px' : '0')} 0 ${frames.tablet} ${margins.m14};
    width: calc(100vw - (${frames.tablet}*2));
  }
  @media (max-width: ${mobileThresholdPixels}) {
    margin: ${(props) => (props.marginTop ? '40px' : '0')} ${frames.mobile} ${margins.m11};
    width: calc(100vw - (${frames.mobile}*2));
  }
`;

const Subtitle = styled.h3`
  padding: 16px;
  background: ${colors.grey100};
  text-transform: uppercase;
  font-size: 32px;
  font-weight: 700;
  margin: 48px 0 0;
  @media (max-width: ${mobileThresholdPixels}) {
    font-size: 24px;
    margin: 24px 0;
    text-transform: none;
  }
`;
const CollapseContainer = styled.div`
  margin-top: 24px;
  display: flex;
  max-width: 1160px;
  justify-content: space-between;
  @media (max-width: ${mobileThresholdPixels}) {
    flex-direction: column;
    gap: 25px;
    max-width: 100%;
    align-items: center;
    margin: 0;
  }
`;
const Collapse = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 24px 16px;
  border-radius: 12px;
  border: 1px solid ${colors.grey600};
`;

const CollapseQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px 16px;
  width: 608px;
  cursor: pointer;
  @media (max-width: ${tabletThresholdPixels}) {
    width: 508px;
    padding: 16px 16px 16px 0;
  }
  @media (max-width: ${mobileThresholdPixels}) {
    width: unset;
    padding: 0;
  }
`;

const CollapseTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Question = styled.h4<{ selected?: boolean }>`
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  max-width: 520px;
  @media (max-width: ${mobileThresholdPixels}) {
    font-size: 14px;
    max-width: 100%;
    ${(props) => props.selected && 'font-weight: 600;'}
  }
`;

const CollapseAnswer = styled.div`
  width: 470px;
  padding: 24px 0;
`;

const ContentTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 40px;
  text-transform: uppercase;
`;

const Answer = styled.p`
  font-size: 12px;
  margin: 8px 0 0;
`;

const ChevronIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const Bullet = styled.div<{ selected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) => props.selected ? colors.black : colors.grey400};
`;

interface FAQBlockProps {
  questions: { question: string, answers: string[], answer?: string }[];
  noTitle?: boolean;
  context: { isMobile: boolean };
  marginTop?: boolean;
  subtitle?: string;
  noMarginBottom?: boolean;
}

function FAQBlock({
  questions, noTitle, context: { isMobile }, marginTop, subtitle, noMarginBottom,
}: FAQBlockProps) {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleCollapse = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const renderDesktop = () => (
    <CollapseContainer>
      <div>
        {questions.map((question, index) => (
          <CollapseQuestion onClick={() => handleCollapse(index)} key={question.question}>
            <CollapseTitle>
              <Bullet selected={openIndex === index} />
              <Question>{question.question}</Question>
            </CollapseTitle>
            <ChevronIcon src={Chevron} alt="chevron" />
          </CollapseQuestion>
        ))}
      </div>
      <CollapseAnswer>
        {questions.map((question, index) => (
          <div key={question.question}>
            {openIndex === index && (
              <>
                <ContentTitle>{question.question}</ContentTitle>
                {question.answer && (
                  <Answer key={question.answer}>
                    {typeof question.answer === 'object'
                      ? question.answer
                      : toFormattedBoldAndItalicText(question.answer)}
                  </Answer>
                )}
                {question.answers?.map((answer) => {
                  if (answer === 'PACKAGE_DELIVERY_FEES') return renderPackagePrice(t, '€', true);
                  if (answer === 'COLISSIMO_INSURANCE') return renderColissimoInsurance(t, true);
                  if (answer === 'MONDIAL_RELAY_INSURANCE') return renderMondialRelayInsurance(t, true);
                  return (
                    <Answer key={answer}>
                      {typeof answer === 'object'
                        ? answer
                        : toFormattedBoldAndItalicText(answer)}
                    </Answer>
                  );
                })}
              </>
            )}
          </div>
        ))}
      </CollapseAnswer>
    </CollapseContainer>
  );

  const renderMobile = () => (
    <CollapseContainer>
      {questions.map((question, index) => (
        <Collapse key={question.question}>
          <CollapseQuestion onClick={() => handleCollapse(index)} key={question.question}>
            <CollapseTitle>
              <Question selected={openIndex === index}>{question.question}</Question>
            </CollapseTitle>
            <ChevronIcon src={Chevron} alt="chevron" />
          </CollapseQuestion>
          {openIndex === index && (
            <>
              {question.answer && (
                <Answer key={question.answer}>
                  {typeof question.answer === 'object'
                    ? question.answer
                    : toFormattedBoldAndItalicText(question.answer)}
                </Answer>
              )}
              {question.answers?.map((answer) => {
                if (answer === 'PACKAGE_DELIVERY_FEES') return renderPackagePrice(t, '€', true);
                if (answer === 'COLISSIMO_INSURANCE') return renderColissimoInsurance(t, true);
                if (answer === 'MONDIAL_RELAY_INSURANCE') return renderMondialRelayInsurance(t, true);
                return (
                  <Answer key={answer}>
                    {typeof answer === 'object'
                      ? answer
                      : toFormattedBoldAndItalicText(answer)}
                  </Answer>
                );
              })}
            </>
          )}
        </Collapse>
      ))}
    </CollapseContainer>
  );

  return (
    <Container marginTop={marginTop} noMarginBottom={noMarginBottom}>
      <Title>{noTitle ? '' : 'FAQ'}</Title>
      <Subtitle>{subtitle || t('Questions fréquentes')}</Subtitle>
      {isMobile ? renderMobile() : renderDesktop()}
    </Container>
  );
}

export default withAppContext(FAQBlock);
